import React, { useState, useEffect } from 'react';
import { 
  Download, 
  Upload, 
  Shield, 
  Lock, 
  AlertTriangle, 
  CheckCircle2, 
  Database,
  FileKey,
  Eye,
  EyeOff,
  Loader2,
  Info,
  HardDrive,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBackup = () => {
  const [backupInfo, setBackupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [restoring, setRestoring] = useState(false);
  
  // Backup password
  const [backupPassword, setBackupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Restore
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const [showRestorePassword, setShowRestorePassword] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [restoreConfirmed, setRestoreConfirmed] = useState(false);

  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`
  });

  const fetchBackupInfo = async () => {
    try {
      const response = await axios.get(`${API}/admin/backup/info`, {
        headers: getAuthHeader()
      });
      setBackupInfo(response.data);
    } catch (error) {
      console.error('Error fetching backup info:', error);
      toast.error('Failed to load backup information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackupInfo();
  }, []);

  const handleDownloadBackup = async () => {
    if (!backupPassword || backupPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (backupPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/admin/backup/download?password=${encodeURIComponent(backupPassword)}`,
        {},
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate
      const contentDisposition = response.headers['content-disposition'];
      let filename = `willkart_backup_${new Date().toISOString().slice(0,10)}.enc`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Backup downloaded successfully! Store it safely along with the password.');
      setBackupPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Backup download failed:', error);
      toast.error('Failed to download backup');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRestoreFile(file);
      setVerificationResult(null);
      setRestoreConfirmed(false);
    }
  };

  const handleVerifyBackup = async () => {
    if (!restoreFile || !restorePassword) {
      toast.error('Please select a backup file and enter the password');
      return;
    }

    setVerifying(true);
    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = btoa(
          new Uint8Array(e.target.result)
            .reduce((data, byte) => data + String.fromCharCode(byte), '')
        );

        try {
          const response = await axios.post(
            `${API}/admin/backup/verify?password=${encodeURIComponent(restorePassword)}&backup_data=${encodeURIComponent(base64Data)}`,
            {},
            { headers: getAuthHeader() }
          );
          
          setVerificationResult(response.data);
          toast.success('Backup verified successfully!');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Verification failed - wrong password or corrupted file');
          setVerificationResult(null);
        } finally {
          setVerifying(false);
        }
      };
      reader.readAsArrayBuffer(restoreFile);
    } catch (error) {
      toast.error('Failed to read backup file');
      setVerifying(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile || !restorePassword || !restoreConfirmed) {
      toast.error('Please verify backup and confirm restoration');
      return;
    }

    setRestoring(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = btoa(
          new Uint8Array(e.target.result)
            .reduce((data, byte) => data + String.fromCharCode(byte), '')
        );

        try {
          const response = await axios.post(
            `${API}/admin/backup/restore?password=${encodeURIComponent(restorePassword)}&backup_data=${encodeURIComponent(base64Data)}&confirm=true`,
            {},
            { headers: getAuthHeader() }
          );
          
          toast.success('Database restored successfully!');
          setRestoreFile(null);
          setRestorePassword('');
          setVerificationResult(null);
          setRestoreConfirmed(false);
          fetchBackupInfo(); // Refresh counts
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Restoration failed');
        } finally {
          setRestoring(false);
        }
      };
      reader.readAsArrayBuffer(restoreFile);
    } catch (error) {
      toast.error('Failed to read backup file');
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-backup">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Data Backup</h1>
        <p className="text-slate-400 mt-1">Download encrypted backups and restore your database</p>
      </div>

      {/* Security Info Card */}
      <Card className="bg-emerald-500/10 border-emerald-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-emerald-400 mt-0.5" />
            <div>
              <h3 className="font-medium text-emerald-400">Military-Grade Encryption</h3>
              <p className="text-sm text-slate-300 mt-1">
                Backups are encrypted using <strong>AES-256-GCM</strong> with your password. 
                The encryption key is derived using <strong>PBKDF2-SHA256</strong> (100,000 iterations).
                Without the correct password, the backup cannot be decrypted.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Download Backup Section */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Download className="w-5 h-5 text-amber-400" />
              Download Backup
            </CardTitle>
            <CardDescription>
              Create an encrypted backup of your entire database
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* What's included */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Database className="w-4 h-4 text-slate-400" />
                Data to be backed up
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {backupInfo?.collections?.map((col) => (
                  <div key={col.collection} className="flex justify-between text-slate-400">
                    <span>{col.display_name}</span>
                    <span className="text-amber-400 font-mono">{col.count}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-700 mt-3 pt-3 flex justify-between text-white font-medium">
                <span>Total Records</span>
                <span className="text-amber-400">{backupInfo?.total_documents}</span>
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300">Encryption Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={backupPassword}
                    onChange={(e) => setBackupPassword(e.target.value)}
                    placeholder="Enter a strong password (min 8 chars)"
                    className="bg-slate-800 border-slate-700 text-white pr-10"
                    data-testid="backup-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Confirm Password</Label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  data-testid="backup-confirm-password"
                />
              </div>
              {backupPassword && confirmPassword && backupPassword !== confirmPassword && (
                <p className="text-red-400 text-sm">Passwords do not match</p>
              )}
              {backupPassword && backupPassword.length < 8 && (
                <p className="text-amber-400 text-sm">Password must be at least 8 characters</p>
              )}
            </div>

            {/* Warning */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-200">
                  <strong>Store your password safely!</strong> Without it, the backup cannot be 
                  decrypted and your data will be permanently unrecoverable.
                </p>
              </div>
            </div>

            <Button
              onClick={handleDownloadBackup}
              disabled={downloading || !backupPassword || backupPassword.length < 8 || backupPassword !== confirmPassword}
              className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900"
              data-testid="download-backup-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Encrypting & Downloading...
                </>
              ) : (
                <>
                  <FileKey className="w-4 h-4 mr-2" />
                  Download Encrypted Backup
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Restore Section */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-400" />
              Restore Backup
            </CardTitle>
            <CardDescription>
              Restore your database from an encrypted backup file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File Upload */}
            <div>
              <Label className="text-slate-300">Backup File (.enc)</Label>
              <div className="mt-1">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-700 rounded-lg cursor-pointer hover:border-slate-600 bg-slate-800/50 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    {restoreFile ? (
                      <>
                        <HardDrive className="w-8 h-8 text-emerald-400 mb-2" />
                        <p className="text-sm text-white">{restoreFile.name}</p>
                        <p className="text-xs text-slate-400">{(restoreFile.size / 1024).toFixed(1)} KB</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-500 mb-2" />
                        <p className="text-sm text-slate-400">Click to select backup file</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".enc"
                    onChange={handleFileSelect}
                    data-testid="restore-file-input"
                  />
                </label>
              </div>
            </div>

            {/* Restore Password */}
            <div>
              <Label className="text-slate-300">Backup Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showRestorePassword ? 'text' : 'password'}
                  value={restorePassword}
                  onChange={(e) => {
                    setRestorePassword(e.target.value);
                    setVerificationResult(null);
                    setRestoreConfirmed(false);
                  }}
                  placeholder="Enter backup password"
                  className="bg-slate-800 border-slate-700 text-white pr-10"
                  data-testid="restore-password"
                />
                <button
                  type="button"
                  onClick={() => setShowRestorePassword(!showRestorePassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showRestorePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Verify Button */}
            <Button
              onClick={handleVerifyBackup}
              disabled={verifying || !restoreFile || !restorePassword}
              variant="outline"
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="verify-backup-btn"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Verify Backup
                </>
              )}
            </Button>

            {/* Verification Result */}
            {verificationResult && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">Backup Verified</span>
                </div>
                <div className="text-sm text-slate-300 space-y-1">
                  <p>Backup Date: <span className="text-white">{new Date(verificationResult.created_at).toLocaleString()}</span></p>
                  <p>Total Records: <span className="text-amber-400 font-mono">{verificationResult.total_records}</span></p>
                </div>
                
                {/* Confirmation Checkbox */}
                <div className="mt-4 pt-4 border-t border-emerald-500/30">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={restoreConfirmed}
                      onChange={(e) => setRestoreConfirmed(e.target.checked)}
                      className="mt-1 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
                      data-testid="restore-confirm-checkbox"
                    />
                    <span className="text-sm text-slate-300">
                      I understand this will <strong className="text-red-400">REPLACE ALL EXISTING DATA</strong> in 
                      the database with the backup data. This action cannot be undone.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Danger Warning */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-200">
                  <strong>Warning:</strong> Restoring will permanently replace all current data. 
                  Create a fresh backup first if needed.
                </p>
              </div>
            </div>

            {/* Restore Button */}
            <Button
              onClick={handleRestore}
              disabled={restoring || !verificationResult || !restoreConfirmed}
              className="w-full bg-red-600 hover:bg-red-500 text-white"
              data-testid="restore-backup-btn"
            >
              {restoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restoring Database...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Restore Database
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Best Practices Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-400" />
            Backup Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <h4 className="font-medium text-white">Frequency</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Daily backups for active businesses</li>
                <li>• Weekly backups at minimum</li>
                <li>• Before any major changes</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-white">Storage</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Store in multiple locations</li>
                <li>• Use cloud storage (Google Drive, Dropbox)</li>
                <li>• Keep offline copies on USB/hard drive</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-white">Password Safety</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Use a password manager</li>
                <li>• Never store password with backup file</li>
                <li>• Write it down in a secure location</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBackup;
